import * as Sentry from '@sentry/react';
import * as React from 'react';
import { updateSimulationContext } from '@/lib/services/sentryEnrichment';
import type {
  StreamingServicePhase,
  WorkspaceSim,
  WorkspaceStatus,
  WorkspaceStreamTarget,
} from '@/lib/types/workspace';
import { extractHostname, isLocalHost } from '@/lib/utils/workspace';
import { useServiceTokens } from './ServiceTokensProvider';
import { readLegacySimStatus } from './simStatus.legacy';
import { useWorkspace } from './WorkspaceProvider';
import { useWorkspaceStatus } from './WorkspaceStatusProvider';

// Sim status, read straight from the sim's own `/sim/status/stream`
// WebSocket — the sim is the single source of truth for its state. The
// browser connects directly (locally to :8081; in cloud via Envoy's
// /sim/status/stream route), so status survives main-thread freezes
// (served from the sim's render-tick cache) and needs no polling: a
// reconnecting socket has no "not ready" error to surface.
//
// Two fallbacks keep this honest without reintroducing polling:
//   * Control plane (WorkspaceStatusProvider.sims) answers "is there a
//     sim?" — including the boot window before the sim's stream accepts
//     connections, and non-livestream sims that never run a stream
//     server at all.
//   * Legacy old pods (predating /sim/status/stream) nest the sim
//     payload in the workspace snapshot; readLegacySimStatus picks it
//     up. See ./simStatus.legacy.

export interface SimStatusContextValue {
  /** Latest sim status payload (sim-direct, or legacy old-pod aggregate), or null. */
  simStatus: WorkspaceStatus | null;
  /** Whether the sim's status is currently observable (direct WS open, or legacy payload present). */
  simReachable: boolean;
  /** Derived streaming readiness for the livestream surface. */
  streamingStatus: StreamingServicePhase;
  /** ``true`` only when a sim is up but was built with ``livestream=False``. */
  streamingDisabled: boolean;
}

const SimStatusContext = React.createContext<SimStatusContextValue | null>(null);

/**
 * Mutable, listener-free store for the simulation clock anchor.
 *
 * The status WebSocket publishes ~4×/sec; if we drove the BottomBar clock
 * directly off React state it would jump in 250 ms steps. Instead the WS
 * message writes a fresh anchor here (no React state, no re-render), and
 * the cell extrapolates forward at ``requestAnimationFrame`` rate to a
 * smooth 60 fps display.
 *
 * The rate at which sim time advances per wall-clock millisecond is
 * inferred from consecutive *playing* anchors and clamped to ``[0, 1000]``:
 *
 *   * ``1000 µs/ms`` is real-time playing — the normal case.
 *   * ``0`` is stopped / paused: a non-playing snapshot sets the rate to 0
 *     directly (not by inference), so the display freezes on the *first*
 *     stopped snapshot. This matters for standalone/CLI sims, which emit a
 *     single post-stop status and then go quiet — inference alone would
 *     leave the clock counting forever off the last playing rate.
 *   * Slower-than-realtime sims (heavy scene at 30 fps) yield a rate
 *     below 1000 and the display advances at the correct fraction.
 *
 * Clamping the inferred rate at 1000 µs/ms means an exceptionally jittery
 * gap between anchors (network blip → 500 ms wall, 250 ms sim time) can
 * NOT briefly accelerate the display — it under-extrapolates a touch and
 * catches up on the next anchor, which is far less jarring than the alternative.
 */
// A *playing* clock that has gone this long without a fresh anchor means the
// sim stopped publishing — closed, wedged, or a half-open socket. Past this we
// freeze the display at the last real anchor instead of extrapolating time the
// sim never actually reached. Generous vs the ~4 Hz publish so jitter never
// trips it; a non-playing clock (rate 0) is already frozen and never clamps.
export const CLOCK_STALE_MS = 1500;

export class SimClockStore {
  private anchorTimeUs = 0;
  private anchorRecvMs = 0;
  private ratePerMs = 1000;
  private initialized = false;

  update(timeUs: number, playing: boolean): void {
    const now = Date.now();
    if (!playing) {
      // Stopped/paused: sim time is frozen, so the display must not advance.
      // Re-anchor to the reported time and zero the rate immediately.
      this.ratePerMs = 0;
    } else if (this.initialized) {
      const dtMs = now - this.anchorRecvMs;
      if (dtMs > 0) {
        const inferred = (timeUs - this.anchorTimeUs) / dtMs;
        this.ratePerMs = Math.max(0, Math.min(1000, inferred));
      }
    } else {
      // First playing anchor: no prior sample to infer from — assume real time
      // until the next anchor corrects it.
      this.ratePerMs = 1000;
    }
    this.anchorTimeUs = timeUs;
    this.anchorRecvMs = now;
    this.initialized = true;
  }

  read(): number {
    if (!this.initialized) return 0;
    const dtMs = Date.now() - this.anchorRecvMs;
    // Freeze a playing clock that has gone stale rather than extrapolating
    // forever off the last rate. A stopped/paused clock has ratePerMs 0, so it
    // is already frozen and this guard is a no-op for it.
    if (this.ratePerMs > 0 && dtMs > CLOCK_STALE_MS) return this.anchorTimeUs;
    return this.anchorTimeUs + dtMs * this.ratePerMs;
  }

  reset(): void {
    this.anchorTimeUs = 0;
    this.anchorRecvMs = 0;
    this.ratePerMs = 1000;
    this.initialized = false;
  }
}

const SimClockContext = React.createContext<SimClockStore | null>(null);

/**
 * Live, bucketed simulation FPS — same isolation trick as {@link SimClockValue}.
 *
 * Quantized to the integer that the BottomBar displays so the context value
 * only changes ~1×/sec (not 10×/sec from the raw float noise), and only the
 * tiny FPS cell that subscribes via {@link useSimFps} re-renders — never the
 * stream surface above.
 */
export interface SimFpsValue {
  /** Simulation FPS rounded to the displayed integer, or null when unknown. */
  fps: number | null;
}

const SimFpsContext = React.createContext<SimFpsValue>({ fps: null });

const SIM_STATUS_PORT = 8081;
// Fixed reconnect cadence (not exponential), so connect latency to a
// fresh sim is bounded by one interval. We only retry while we should
// actually be connected (see shouldConnectSimDirect) — local
// connect-until-up, or a cloud sim that's booting — so a steady 1 s is
// right and there's no idle retry loop to back off.
const RECONNECT_RETRY_MS = 1_000;
// A *playing* sim publishes anchors at ~4 Hz. If one goes silent this long the
// socket is half-open / the sim closed or wedged (a clean close stops the push
// loop without sending a WS close frame, so onclose never fires on its own).
// Force-close past this so the normal onclose teardown runs. Comfortably above
// the publish interval so ordinary jitter never trips it. Only applied while
// the last snapshot was 'playing' — a stopped/idle sim quiesces on purpose.
const STATUS_STALE_MS = 2_500;

/**
 * Whether to open (and keep retrying) the sim-direct status WS.
 *
 * Adapts to whatever config is present, keyed on whether a control
 * plane is actually reporting (not on local-vs-cloud): until the
 * workspace status snapshot has arrived — local with no workspace
 * server, or the brief pre-first-snapshot window — connect-until-up so
 * an already-running sim connects with zero dependency. Once the
 * control plane IS reporting, gate on its registry: only attempt when
 * it lists a livestream sim whose status server is bound, so an idle tab
 * never fires a failed handshake (no console noise, no TLS churn) when no
 * sim is running, and a still-cold-booting sim shows "Waiting for
 * simulation…" without churning the socket before its server accepts.
 * Old pods (legacy payload present) never use this endpoint.
 *
 * @param opts.hasWsUrl - Whether a target + tokens yield a WS URL.
 * @param opts.isLegacyPod - Whether the workspace nests the sim payload (old image).
 * @param opts.hasControlPlane - Whether a workspace status snapshot has arrived.
 * @param opts.livestreamSimConnectable - Whether the registry lists a
 *   connectable (status-server-ready) livestream sim. This is the
 *   readiness-filtered value, not raw presence: a cold-booting sim is present
 *   but not yet connectable, so the socket stays closed until its server binds.
 * @returns Whether to maintain the sim-direct connection.
 */
export function shouldConnectSimDirect(opts: {
  hasWsUrl: boolean;
  isLegacyPod: boolean;
  hasControlPlane: boolean;
  livestreamSimConnectable: boolean;
}): boolean {
  if (!opts.hasWsUrl || opts.isLegacyPod) return false;
  return !opts.hasControlPlane || opts.livestreamSimConnectable;
}

/**
 * Decide whether a socket close should release the sticky "ever
 * connected" attach (and drop the served payload).
 *
 * Keyed on *connectable* (status-server-ready), not raw presence: after
 * a sim restart the replacement advertises itself with
 * ``status_server_ready: false`` for the whole pre-bind window, and a
 * presence-keyed latch would keep hammering failed handshakes against
 * the unbound server — the exact churn :func:`shouldConnectSimDirect`
 * gates against for the first connect. Releasing on a transient
 * registry blip is safe: a blip degrades presence and connectable
 * together, release only stops reconnect *attempts* (an open socket is
 * never closed by it), and per-entry ``status_server_ready`` only ever
 * transitions false→true, so connectable cannot flap for a live sim.
 *
 * @param opts.hasControlPlane - Whether a workspace status snapshot has arrived.
 * @param opts.livestreamSimConnectable - Whether the registry lists a
 *   connectable (status-server-ready) livestream sim.
 * @returns Whether to release the sticky attach.
 */
export function shouldReleaseSimDirectAttach(opts: {
  hasControlPlane: boolean;
  livestreamSimConnectable: boolean;
}): boolean {
  return opts.hasControlPlane && !opts.livestreamSimConnectable;
}

function simStatusWsUrl(target: WorkspaceStreamTarget, workspaceToken: string | undefined): string {
  const hostname = extractHostname(target.httpBaseUrl);
  if (isLocalHost(hostname)) return `ws://${hostname}:${SIM_STATUS_PORT}/sim/status/stream`;
  const url = new URL('/sim/status/stream', `https://${hostname}`);
  url.protocol = 'wss:';
  if (workspaceToken) url.searchParams.set('token', workspaceToken);
  return url.toString();
}

/** Derive streaming readiness from the sim's own status payload. */
export function streamingFromPayload(payload: WorkspaceStatus): StreamingServicePhase {
  if (payload.livestream === false) return 'disabled';
  if (payload.state === 'starting') return 'starting';
  // Past 'starting' on a livestream sim means the stream is up: the sim's
  // boot blocks on wait_for_signaling_server (and raises on timeout), so it
  // never publishes a non-'starting' snapshot until the signaling server is
  // bound. No separate readiness flag needed.
  return 'ready';
}

/** Derive streaming readiness from the control-plane sims registry alone (no sim-direct payload yet). */
export function streamingFromRegistry(sims: WorkspaceSim[]): StreamingServicePhase {
  if (sims.some((sim) => sim.livestream)) return 'starting';
  if (sims.length > 0 && sims.every((sim) => !sim.livestream)) return 'disabled';
  return 'unknown';
}

/**
 * Whether a livestream sim is present AND its status server is accepting
 * connections (``status_server_ready``). Gates the sim-direct
 * ``/sim/status/stream`` connect: the registry entry is advertised at the
 * very start of cold boot so the surface can show "Waiting for simulation…",
 * but opening the socket before the status server binds only churns failed
 * handshakes. Pods predating the field omit it → ``undefined`` is treated as
 * ready for backward compatibility.
 */
export function livestreamSimReady(sims: WorkspaceSim[]): boolean {
  return sims.some((sim) => sim.livestream && sim.status_server_ready !== false);
}

export function SimStatusProvider({ children }: { children: React.ReactNode }) {
  const { streamTarget } = useWorkspace();
  const { tokens: serviceTokens, isLoading: tokensLoading } = useServiceTokens();
  const { snapshot, sims, refresh: refreshControlPlane } = useWorkspaceStatus();

  const [directPayload, setDirectPayload] = React.useState<WorkspaceStatus | null>(null);
  const [directOpen, setDirectOpen] = React.useState(false);

  // The clock store lives outside React state so the provider doesn't
  // re-render the BottomBar leaf on every status tick — the WS handler
  // writes the anchor, the cell extrapolates via rAF in useSimClockTime.
  const simClockStore = React.useMemo(() => new SimClockStore(), []);

  const isLocal = streamTarget ? isLocalHost(extractHostname(streamTarget.httpBaseUrl)) : false;
  const workspaceToken = isLocal ? undefined : (serviceTokens?.workspace_token ?? undefined);
  const tokensReady = isLocal || (!tokensLoading && serviceTokens !== null);

  // Old pods nest the sim payload in the workspace snapshot. When that's
  // present, defer to it and don't attempt the sim-direct WS (the
  // endpoint doesn't exist on those images — connecting would only emit
  // failed-handshake noise).
  const legacy = React.useMemo(() => readLegacySimStatus(snapshot), [snapshot]);

  // Open the sim's own status stream — the single source of truth, with its
  // own reconnect + on-connect seed (server seeds the cached snapshot on
  // open). The control-plane registry decides only the FIRST connect (so an
  // idle tab stays silent until a sim exists) and the FINAL release (sim
  // confirmed gone). It must NOT tear down a live socket on a transient
  // registry gap (a stale read degrading to "no sims", a restart race) — the
  // open socket is itself the liveness signal. Subordinating the WS to the
  // registry was the root of the recurring "Waiting for simulation…" that a
  // refresh briefly cleared. See the connect effect below.
  const wsUrl = streamTarget && tokensReady ? simStatusWsUrl(streamTarget, workspaceToken) : null;
  const livestreamSimPresent = sims.some((sim) => sim.livestream);
  // Connect-gate signal: a livestream sim whose status server is bound. Distinct
  // from ``livestreamSimPresent`` (raw presence, used for the sim-gone release
  // and served-session fallback) so a sim that is advertised-but-still-booting
  // shows "Waiting for simulation…" without the WS churning failed handshakes.
  const livestreamSimConnectable = livestreamSimReady(sims);
  const isLegacyPod = legacy !== null;
  const hasControlPlane = snapshot !== null;

  // Latest control-plane signals mirrored to refs so the long-lived status-WS
  // effect reads them in its async callbacks without listing them as deps —
  // re-running the effect on every registry tick would tear down a healthy
  // socket. ``everConnectedRef`` latches once a socket opens and is released
  // only when the sim is confirmed gone (see onclose).
  const hasControlPlaneRef = React.useRef(hasControlPlane);
  const livestreamSimPresentRef = React.useRef(livestreamSimPresent);
  const livestreamSimConnectableRef = React.useRef(livestreamSimConnectable);
  const everConnectedRef = React.useRef(false);
  React.useEffect(() => {
    hasControlPlaneRef.current = hasControlPlane;
    livestreamSimPresentRef.current = livestreamSimPresent;
    livestreamSimConnectableRef.current = livestreamSimConnectable;
  }, [hasControlPlane, livestreamSimPresent, livestreamSimConnectable]);

  // Drop the cached payload when the target changes so a socket opened
  // for a new workspace never briefly republishes the previous
  // workspace's status before its first seed arrives.
  React.useEffect(() => {
    void wsUrl; // intentional trigger: clear whenever the target changes
    setDirectPayload(null);
    setDirectOpen(false);
    everConnectedRef.current = false;
    simClockStore.reset();
  }, [wsUrl, simClockStore]);

  React.useEffect(() => {
    if (!wsUrl || isLegacyPod) {
      setDirectOpen(false);
      setDirectPayload(null);
      everConnectedRef.current = false;
      return;
    }

    let cancelled = false;
    let socket: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    // Staleness watchdog: a playing sim must keep streaming anchors. If it goes
    // silent the socket is force-closed (see STATUS_STALE_MS). Updated on every
    // message; lastWasPlaying gates the watchdog so an idle/stopped sim, which
    // legitimately stops publishing, is never torn down.
    let lastMessageAt = 0;
    let lastWasPlaying = false;
    let watchdog: ReturnType<typeof setInterval> | null = null;

    // Should we hold an open socket right now? Connect-until-up before the
    // control plane reports, gate on the registry once it does — but stay
    // attached once we've ever connected, so a registry blip can't drop a
    // healthy stream. Released only in onclose when the sim is confirmed gone.
    const wantConnect = () =>
      shouldConnectSimDirect({
        hasWsUrl: true,
        isLegacyPod: false,
        hasControlPlane: hasControlPlaneRef.current,
        livestreamSimConnectable: livestreamSimConnectableRef.current,
      }) || everConnectedRef.current;

    const connect = () => {
      if (cancelled) return;
      // Nothing to attach to yet (idle tab, control plane reports no sim, and
      // we've never connected). Re-check on the same cadence — no socket
      // opened, so no failed handshake / TLS churn — until a sim appears.
      if (!wantConnect()) {
        reconnectTimer = setTimeout(connect, RECONNECT_RETRY_MS);
        return;
      }
      socket = new WebSocket(wsUrl);
      let opened = false;
      socket.onopen = () => {
        opened = true;
        everConnectedRef.current = true;
        lastMessageAt = Date.now();
        setDirectOpen(true);
      };
      socket.onmessage = (event) => {
        // A message already queued on the old socket can dispatch after this
        // effect's cleanup (target change). Drop it so a stale payload can't
        // briefly publish the previous workspace's sim status for the new target.
        if (cancelled) return;
        // Text frames arrive as strings; skip anything else (defensive).
        if (typeof event.data !== 'string') return;
        let parsed: WorkspaceStatus | null = null;
        try {
          parsed = JSON.parse(event.data) as WorkspaceStatus;
        } catch {
          return;
        }
        if (!parsed || typeof parsed !== 'object') return;
        // Stamp the clock anchor *before* the React state update so the
        // rAF reader sees the fresh anchor on the next animation frame
        // even if React batches setState. No setState here — the cell
        // pulls from the anchor itself, not from a context value change.
        // A 'starting' (or time_us-less) payload carries no clock — reset so a
        // restart reusing this socket can't extrapolate the previous session's
        // time during boot.
        if (parsed.state === 'starting' || typeof parsed.time_us !== 'number') {
          simClockStore.reset();
        } else {
          simClockStore.update(parsed.time_us, parsed.state === 'playing');
        }
        lastMessageAt = Date.now();
        lastWasPlaying = parsed.state === 'playing';
        setDirectPayload(parsed);
      };
      socket.onerror = () => {
        socket?.close();
      };
      socket.onclose = () => {
        if (cancelled) return;
        if (opened) {
          setDirectOpen(false);
          // The sim's status stream just dropped after being live. Eagerly
          // re-read the control plane so a killed/terminated sim is swept from
          // the registry within ~1 s (vs. waiting for the next periodic tick).
          // That lets the viewport cover the now-dead frame and show "No
          // simulation is running" promptly instead of holding a black/frozen
          // frame for the full stale-frame grace. Harmless on a transient blip:
          // the registry re-read still lists a live sim, so nothing changes.
          refreshControlPlane('sim-status-ws-closed');
        }
        // Sim confirmed unreachable: release the sticky attach and drop the
        // stale payload so the surface falls back to the registry view and we
        // stop chasing a dead (or not-yet-bound) endpoint. See
        // :func:`shouldReleaseSimDirectAttach` for why this keys on
        // connectable rather than raw presence. Otherwise (a connectable sim
        // is still listed, or a transient registry gap) keep reconnecting —
        // the sim is up.
        if (
          shouldReleaseSimDirectAttach({
            hasControlPlane: hasControlPlaneRef.current,
            livestreamSimConnectable: livestreamSimConnectableRef.current,
          })
        ) {
          everConnectedRef.current = false;
          setDirectPayload(null);
        }
        reconnectTimer = setTimeout(connect, RECONNECT_RETRY_MS);
      };
    };

    connect();

    // A playing sim that stops streaming anchors has closed/wedged/gone
    // half-open. Force-close so the existing onclose path drops the payload,
    // marks disconnected, and refreshes the control plane (covering the frozen
    // frame). Gated on lastWasPlaying so a healthy stopped/idle sim — which
    // legitimately quiesces — is never disconnected.
    watchdog = setInterval(() => {
      if (cancelled || !socket || socket.readyState !== WebSocket.OPEN) return;
      if (lastWasPlaying && Date.now() - lastMessageAt > STATUS_STALE_MS) {
        socket.close();
      }
    }, 1_000);

    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (watchdog) clearInterval(watchdog);
      setDirectOpen(false);
      socket?.close();
    };
    // Deps are only the socket-identity inputs (target URL, legacy gate), the
    // stable clock store, and the stable control-plane refresh callback — NOT
    // the registry signals (read via refs), so a registry tick never re-runs
    // the effect and tears down a live socket.
  }, [wsUrl, isLegacyPod, simClockStore, refreshControlPlane]);

  // Keep serving the last sim-direct payload through a transient WS reconnect,
  // but only while the registry still lists THE SAME sim (matched by
  // session_id) — not merely "some livestream sim is present". Same-session
  // continuity is what keeps a healthy stream alive across a status-WS blip
  // (otherwise the surface drops to registry-only 'starting', which
  // deriveStreamState reads as shouldForceDisconnect and tears down a healthy
  // live stream). Requiring the *served* session to still be listed also stops
  // a sim→sim swap — e.g. a livestream sim replaced by a headless one — from
  // rendering the previous sim's frozen frame + stale status overlay during the
  // reconnect window: the registry already lists the new sim, not the one this
  // payload came from, so the cached payload stops being usable immediately
  // instead of lingering until the reconnected socket's first message. Older
  // sims that don't report session_id fall back to the coarse presence check.
  const servedSessionId = directPayload?.session_id ?? null;
  const servedSimStillListed =
    servedSessionId !== null ? sims.some((sim) => sim.session_id === servedSessionId) : livestreamSimPresent;
  const directUsable = directPayload !== null && (directOpen || servedSimStillListed);
  const live = directUsable ? directPayload : null;

  // Coarse signature: the fields the stream surface actually reacts to. The
  // high-frequency fields (time_us, fps, exact uptime) advance every tick but
  // are deliberately excluded so the coarse value keeps a STABLE identity —
  // the live clock reads time_us via SimClockContext, fps is published through
  // SimFpsContext (bucketed to the displayed integer), and uptime collapses to
  // a single ≥1s threshold flip. This is what stops StreamOnlyWindow (+
  // AppStream + overlays) from reconciling at the 10 Hz publish rate (which in
  // dev was the root of the long-task spikes — React DevTools profiling
  // instrumentation emits a `performance.measure()` per fiber on commit, and
  // those entries are never cleared). viewport_width/height ARE included: the
  // resize-ACK gate compares the sim's echoed dimensions against the requested
  // resize, so the coarse snapshot must update when they change or the echo is
  // never observed (the gate would always fall back to its timeout). They only
  // move on an actual viewport resize, not every tick, so identity stays stable.
  const coarseKey = live
    ? [
        live.state,
        live.healthy,
        live.livestream,
        live.session_id,
        live.quality_pixel_budget,
        live.startup_phase,
        live.kernel_managed,
        live.viewport_width,
        live.viewport_height,
        (live.uptime_s ?? 0) >= 1 ? 1 : 0,
      ].join('|')
    : 'none';
  // biome-ignore lint/correctness/useExhaustiveDependencies: coarseKey is the equality signature for `live`; re-snapshot only when it changes.
  const coarseStatus = React.useMemo(() => live, [coarseKey]);

  // Sim diagnostics run here at the live source — off StreamOnlyWindow's render
  // path. The provider has no heavy children to reconcile (children bail when
  // its value changes), so running this at the publish rate is cheap.
  const lastSessionIdRef = React.useRef<string | null>(null);
  const lastFrameCountRef = React.useRef<number | null>(null);
  const frameStallCountRef = React.useRef(0);
  React.useEffect(() => {
    if (!live) {
      lastFrameCountRef.current = null;
      frameStallCountRef.current = 0;
      updateSimulationContext(null);
      return;
    }

    const sessionId = live.session_id ?? null;
    const prevSessionId = lastSessionIdRef.current;
    if (prevSessionId !== null && sessionId !== null && prevSessionId !== sessionId) {
      Sentry.captureMessage('Simulation session changed', {
        level: 'info',
        fingerprint: ['sim-session-changed'],
        extra: {
          oldSessionId: prevSessionId,
          newSessionId: sessionId,
          simState: live.state,
          simFps: live.fps ?? null,
          simUptimeS: live.uptime_s ?? null,
          kernelManaged: live.kernel_managed ?? null,
        },
      });
    }
    lastSessionIdRef.current = sessionId;

    const frameCount = live.frame_count ?? null;
    if (
      frameCount !== null &&
      lastFrameCountRef.current !== null &&
      frameCount === lastFrameCountRef.current &&
      live.state === 'playing'
    ) {
      frameStallCountRef.current++;
      if (frameStallCountRef.current === 3) {
        Sentry.captureMessage('Simulation frame stall detected', {
          level: 'warning',
          fingerprint: ['sim-frame-stall'],
          extra: {
            stalledFrameCount: frameCount,
            consecutiveStalledPolls: frameStallCountRef.current,
            simSessionId: live.session_id ?? null,
            simFps: live.fps ?? null,
            simUptimeS: live.uptime_s ?? null,
          },
        });
      }
    } else {
      frameStallCountRef.current = 0;
    }
    lastFrameCountRef.current = frameCount;

    updateSimulationContext({
      sessionId: live.session_id ?? null,
      state: live.state,
      fps: live.fps ?? null,
      uptimeS: live.uptime_s ?? null,
      kernelManaged: live.kernel_managed ?? null,
    });
  }, [live]);

  const streamingStatus: StreamingServicePhase = coarseStatus
    ? streamingFromPayload(coarseStatus)
    : legacy
      ? legacy.streamingStatus
      : streamingFromRegistry(sims);

  const value = React.useMemo<SimStatusContextValue>(() => {
    if (coarseStatus) {
      return {
        simStatus: coarseStatus,
        simReachable: true,
        streamingStatus,
        streamingDisabled: streamingStatus === 'disabled',
      };
    }
    if (legacy) {
      return {
        simStatus: legacy.simStatus,
        simReachable: legacy.simReachable,
        streamingStatus,
        streamingDisabled: streamingStatus === 'disabled',
      };
    }
    return { simStatus: null, simReachable: false, streamingStatus, streamingDisabled: streamingStatus === 'disabled' };
  }, [coarseStatus, legacy, streamingStatus]);

  // Legacy old-pod fallback for the clock: those workspaces don't carry a
  // direct WS, so we anchor the store from the workspace-status payload
  // whenever the live path is empty.
  const legacyTimeUs = !live && typeof legacy?.simStatus?.time_us === 'number' ? legacy.simStatus.time_us : null;
  const legacyPlaying = legacy?.simStatus?.state === 'playing';
  React.useEffect(() => {
    if (legacyTimeUs !== null) simClockStore.update(legacyTimeUs, legacyPlaying);
  }, [legacyTimeUs, legacyPlaying, simClockStore]);

  // Live FPS, bucketed to the displayed integer so the cell only re-renders on
  // a visible change. Same legacy fallback as the clock.
  const liveFps = (live ?? legacy?.simStatus)?.fps;
  const fpsBucket = typeof liveFps === 'number' ? Math.round(liveFps) : null;
  const fps = React.useMemo<SimFpsValue>(() => ({ fps: fpsBucket }), [fpsBucket]);

  return (
    <SimStatusContext.Provider value={value}>
      <SimClockContext.Provider value={simClockStore}>
        <SimFpsContext.Provider value={fps}>{children}</SimFpsContext.Provider>
      </SimClockContext.Provider>
    </SimStatusContext.Provider>
  );
}

export function useSimStatus(): SimStatusContextValue {
  const value = React.useContext(SimStatusContext);
  if (!value) throw new Error('useSimStatus must be used within SimStatusProvider');
  return value;
}

/**
 * Smoothly-extrapolated simulation time in microseconds. Drives the
 * BottomBar clock at the browser's animation rate (~60 fps) regardless of
 * the underlying status-WS publish rate (currently 4 Hz, see
 * ``STATUS_PUBLISH_INTERVAL_S`` in the sim).
 *
 * Mechanics: subscribes to the surrounding ``SimClockStore`` and runs a
 * ``requestAnimationFrame`` loop while ``active`` is true; each frame it
 * reads the store's interpolated value and updates local state. React
 * bails on identical primitive setStates so a paused / stopped sim
 * (where the inferred rate clamps to 0) re-renders the consumer zero
 * times — no work, no DOM updates.
 *
 * @param active - ``false`` short-circuits the rAF loop entirely. Pass
 *   ``false`` whenever the consumer is going to render a placeholder
 *   (e.g. status server unreachable) so we don't burn frames computing
 *   a value that won't be shown.
 * @returns Current simulation time in microseconds.
 */
export function useSimClockTime(active: boolean): number {
  const store = React.useContext(SimClockContext);
  const [timeUs, setTimeUs] = React.useState<number>(() => bucketToTenth(store?.read() ?? 0));
  React.useEffect(() => {
    if (!active || !store) return;
    let raf = 0;
    const tick = () => {
      // The clock is only displayed to a tenth of a second, so bucket the
      // microsecond-precise read down to 100 ms. setTimeUs with the previous
      // value bails out at the React level (Object.is), so the cell re-renders
      // ~10×/sec instead of once per animation frame — and a paused sim (where
      // the read is constant) does no work past the read at all.
      const next = bucketToTenth(store.read());
      setTimeUs((prev) => (prev === next ? prev : next));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, store]);
  return timeUs;
}

// Floor a microsecond timestamp to the nearest tenth of a second (100 ms),
// the resolution the BottomBar clock actually displays.
function bucketToTenth(timeUs: number): number {
  return Math.floor(timeUs / 100_000) * 100_000;
}

/**
 * Live sim FPS (bucketed to the displayed integer, so ~1×/sec changes). Same
 * isolation contract as {@link useSimClock}: consume only from a memoized leaf.
 */
export function useSimFps(): SimFpsValue {
  return React.useContext(SimFpsContext);
}
